<script>
    import { onMount } from 'svelte';
    import { getApiUrl } from '../api';
    import { t } from '../i18nStore';
    import { Link } from 'svelte-routing';
    import { selectedCountry } from '../countryStore';
    import PromotionCard from '../components/PromotionCard.svelte'; // Use standard card view

    let specials = [];
    let isLoading = true;
    let error = '';

    // Re-fetch automatically if the user changes the country selector
    $: if ($selectedCountry) {
        fetchSpecials();
    }

    async function fetchSpecials() {
        isLoading = true;
        error = '';
        try {
            const res = await fetch(getApiUrl(`/promotions/today-special?countryCode=${$selectedCountry}`));
            if (!res.ok) throw new Error("Failed to fetch today's specials");
            
            const data = await res.json();
            // Fallback to empty array if data is null
            specials = data || []; 
        } catch (e) {
            error = e.message;
        } finally {
            isLoading = false;
        }
    }
</script>

<div class="specials-container">
    <div class="header">
        <h1>✨ {$t('promotions.today_special_title') || "Today's Special"}</h1>
        <p>{new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
    </div>

    {#if isLoading}
        <div class="state-message">
            <div class="spinner"></div>
            <p>{$t('home.loading') || 'Loading specials...'}</p>
        </div>
    {:else if error}
        <div class="error-box">
            <p>{error}</p>
            <button on:click={fetchSpecials}>Retry</button>
        </div>
    {:else if specials.length === 0}
        <div class="empty-state">
            <p>{$t('promotions.no_specials') || 'No specific special offers for today. Check out our general promotions!'}</p>
            <Link to="/" class="browse-btn">{$t('promotions.browse_all_promotions') || 'Browse All Promotions'}</Link>
        </div>
    {:else}
        <!-- Using the requested PromotionCard view in a responsive grid -->
        <div class="specials-grid">
            {#each specials as promo (promo._id)}
                <PromotionCard {promo} showStartDate={false} />
            {/each}
        </div>
    {/if}
</div>

<style>
    .specials-container { max-width: 1200px; margin: 2rem auto; padding: 0 1rem; }
    .header { text-align: center; margin-bottom: 3rem; }
    .header h1 { color: #2d3748; margin-bottom: 0.5rem; font-size: 2rem; }
    .header p { color: #718096; font-weight: 500; font-size: 1.1rem; }

    .specials-grid { 
        display: grid; 
        grid-template-columns: 1fr; 
        gap: 1.5rem; 
    }

    @media (min-width: 600px) {
        .specials-grid {
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
        }
    }

    .state-message, .empty-state { 
        text-align: center; 
        padding: 5rem 2rem; 
        background: #fff; 
        border-radius: 12px; 
        box-shadow: 0 2px 10px rgba(0,0,0,0.05);
    }
    
    .spinner {
        border: 4px solid rgba(0,0,0,0.1);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border-left-color: #007bff;
        animation: spin 1s linear infinite;
        margin: 0 auto 1rem;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .browse-btn { 
        display: inline-block; 
        margin-top: 1.5rem; 
        padding: 0.8rem 2rem;
        background-color: #007bff;
        color: white;
        text-decoration: none;
        border-radius: 6px;
        font-weight: 600;
    }

    .error-box { color: #dc3545; text-align: center; padding: 3rem; background: #fff5f5; border-radius: 8px; }
    .error-box button { margin-top: 1rem; padding: 0.5rem 1.5rem; cursor: pointer; }
</style>